import type { BlogPosting, WithContext } from 'schema-dts'
import type { StrapiMagazineArticle } from '~/composables/types/strapi/strapi-magazine'
import type { Supplier } from '~/composables/types/event'

const schemaDateFormat = 'YYYY-MM-DD'

export function useMagazineArticleSchema(
  article: StrapiMagazineArticle,
  supplier?: Supplier,
  overwrites: any = {}
): any {
  const $i18n = useI18n()
  const $fullLocalizedUrl = useFullLocalizedUrl()

  const schema = ref<WithContext<BlogPosting> | undefined>(undefined)
  const script = computed(() => ({
    type: 'application/ld+json',
    innerHTML: schema.value,
  }))

  const createSchema = () => {
    const articleSection = articleCategoryGetters.getTitle(
      articleGetters.getFirstCategory(article)
    )
    const obj = {
      '@type': 'BlogPosting',
      headline: articleGetters.getTitle(article),
      image: [articleGetters.getHeaderImageUrl(article)],
      datePublished: formatDate(
        articleGetters.getCreatedAt(article),
        $i18n.locale.value,
        schemaDateFormat
      ),
      dateModified: formatDate(
        articleGetters.getUpdatedAt(article),
        $i18n.locale.value,
        schemaDateFormat
      ),
      author: [],
      // dateModified: "2022-12-12",
      // only include articleSection when not null
      ...(articleSection ? { articleSection } : {}),
      publisher: {
        '@type': 'Organization',
        name: 'konfetti',
        logo: 'https://gokonfetti.com/logos/full-logo-color-black.svg',
        url: 'https://gokonfetti.com/de-de/',
        telephone: '+4930257706800',
      },
      ...overwrites,
    }

    if (supplierGetters.getId(supplier)) {
      obj.author.push({
        '@type': 'Organization',
        name: supplierGetters.getName(supplier),
        ...(supplierGetters.getPermalink(supplier) && {
          url: $fullLocalizedUrl(
            `/p/${supplierGetters.getPermalink(supplier)}`
          ),
        }),
        ...(supplierGetters.getLogo(supplier) && {
          logo: supplierGetters.getLogo(supplier),
        }),
        ...(supplierGetters.getAddress(supplier) && {
          address: supplierGetters.getAddress(supplier),
        }),
      })
    } else {
      obj.author.push({
        '@type': 'Person',
        name: articleGetters.getAuthor(article),
        ...(articleGetters.getAuthorUrl(article) && {
          url: articleGetters.getAuthorUrl(article),
        }),
      })
    }

    return obj
  }

  schema.value = { '@context': 'https://schema.org', '@graph': createSchema() }

  return {
    schema,
    script,
  }
}
